import { CommonModule } from "@angular/common";
import { Component, OnInit, ViewChild } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { MeetingService, AmplitudeService } from "../../core/services";
import { take } from "rxjs";
import { EmailModalComponent, InfoPopupComponent, MainWrapperComponent } from "@core/component";

@Component({
    selector: 'app-meetings',
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        ReactiveFormsModule,
        MainWrapperComponent,
        EmailModalComponent,
        InfoPopupComponent,
        FormsModule,
    ],
    templateUrl: './meetings.component.html',
    styleUrls: ['./meetings.component.scss'],  // fixed typo here
})
export class MeetingsComponent implements OnInit {
    @ViewChild('emailModal') private _emailModal!: EmailModalComponent;
    @ViewChild('infoModal') private _infoModal!: InfoPopupComponent;

    allMeetings: any[] = [];
    meetingBotId = '';
    collapsedDates: Set<string> = new Set();
    allExpanded = true;
    selectedEmails: string[] = [];

    // ── Pagination ────────────────────────────────────────────────────────
    private readonly _pageSize = 20;
    currentPage = 1;
    isLoading = false;
    hasNextPage = true;

    // ── Date range filter ──────────────────────────────────────────────
    filterFrom = '';
    filterTo = '';
    searchQuery = '';
    // draft values bound to the inputs before user hits Apply
    draftFrom = '';
    draftTo = '';
    draftSearch = '';
    private _searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

    dropsDown = 'down';
    opensRight = 'right';
    selectedSimpleCalendarTimeUpRight: any;
    meetingSortOrder: { [date: string]: boolean } = {};
    private readonly _localDateKeyFormatter = new Intl.DateTimeFormat('en-CA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
    private readonly _localDateLabelFormatter = new Intl.DateTimeFormat(undefined, {
        weekday: 'long',
        month: 'short',
        day: '2-digit',
    });
    private readonly _localTimeFormatter = new Intl.DateTimeFormat(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });

    constructor(
        private readonly _meetingService: MeetingService,
        private readonly _route: ActivatedRoute,
        private readonly _router: Router,
        private readonly _amplitudeService: AmplitudeService
    ) { }

    ngOnInit(): void {
        // React to ALL query param changes — page, from, to
        this._route.queryParamMap.subscribe(params => {
            const page = Math.max(1, parseInt(params.get('page') ?? '1', 10) || 1);
            this.filterFrom = params.get('from') ?? '';
            this.filterTo   = params.get('to')   ?? '';
            this.searchQuery = (params.get('q') ?? '').trim();
            // Keep draft inputs in sync with URL
            this.draftFrom = this.filterFrom;
            this.draftTo   = this.filterTo;
            this.draftSearch = this.searchQuery;
            this._loadPage(page);
        });
    }

    private _loadPage(page: number): void {
        this.isLoading = true;
        const offset = (page - 1) * this._pageSize;
        this._meetingService
            .getCompletedOpportunityMeetings(
                this._pageSize,
                offset,
                this.filterFrom || null,
                this.filterTo || null,
                this.searchQuery || null
            )
            .pipe(take(1)).subscribe({
            next: (response) => {
                this.isLoading = false;
                const rows: any[] = Array.isArray(response) ? response : [];

                // Out-of-range page (e.g. ?page=9999) — redirect to page 1
                if (rows.length === 0 && page > 1) {
                    this._navigate(1);
                    return;
                }

                this.hasNextPage = rows.length === this._pageSize;
                this.currentPage = page;
                this.collapsedDates.clear();
                this.allExpanded = true;
                this.meetingSortOrder = {};
                this.allMeetings = this._buildGroups(rows);
            },
            error: () => { this.isLoading = false; }
        });
    }

    private _buildGroups(rows: any[]): any[] {
        const map: Record<string, any[]> = {};
        rows.forEach(meeting => {
            const date = this._getMeetingDateKey(meeting);
            if (!date) return;
            meeting.isMeetingHappened = !!meeting.actual_duration_minutes && !!meeting.distinct_speakers?.length;
            if (!map[date]) { map[date] = []; this.meetingSortOrder[date] = true; }
            map[date].push(meeting);
        });
        return Object.keys(map)
            .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
            .map(date => ({
                date,
                meetings: map[date].sort((a: any, b: any) => {
                    const startA = this._getMeetingSortMs(a);
                    const startB = this._getMeetingSortMs(b);
                    return startA - startB;
                })
            }));
    }

    goToPrevPage(): void {
        if (this.currentPage > 1) this._navigate(this.currentPage - 1);
    }

    goToNextPage(): void {
        if (this.hasNextPage) this._navigate(this.currentPage + 1);
    }

    applyDateFilter(): void {
        this._navigate(1, this.draftFrom, this.draftTo);
    }

    clearDateFilter(): void {
        this.draftFrom = '';
        this.draftTo   = '';
        this._navigate(1, '', '');
    }

    onSearchChange(value: string): void {
        this.draftSearch = value;
        if (this._searchDebounceTimer) {
            clearTimeout(this._searchDebounceTimer);
        }
        this._searchDebounceTimer = setTimeout(() => {
            this._navigate(1, this.filterFrom, this.filterTo, this.draftSearch.trim());
        }, 300);
    }

    get isFilterActive(): boolean {
        return !!(this.filterFrom || this.filterTo);
    }

    private _navigate(page: number, from = this.filterFrom, to = this.filterTo, q = this.searchQuery): void {
        this._router.navigate([], {
            relativeTo: this._route,
            queryParams: {
                page,
                from: from || null,
                to:   to   || null,
                q:    q    || null,
            },
            queryParamsHandling: 'merge',
        });
    }

    isCollapsed(date: string): boolean {
        return this.collapsedDates.has(date);
    }

    toggleCollapse(date: string): void {
        if (this.collapsedDates.has(date)) {
            this.collapsedDates.delete(date);
        } else {
            this.collapsedDates.add(date);
        }
    }

    toggleExpandCollapse(): void {
        if (this.allExpanded) {
            this.collapsedDates = new Set(this.allMeetings.map(m => m.date));
        } else {
            this.collapsedDates.clear();
        }
        this.allExpanded = !this.allExpanded;
    }

    formatDuration(minutes: number): string {
        if (!minutes) return 'N/A';
        const hours = Math.floor(minutes / 60);
        const mins = Math.floor(minutes % 60);
        return hours > 0 ? `${hours}h ${mins}` : `${mins}`;
    }

    formatDurationMinutes(minutes: number): string {
        if (!minutes) return 'N/A';
        return `${Math.round(minutes)}'`;
    }

    goToOpportunity(meeting: any): void {
        this._amplitudeService.trackEvent('my_past_meeting_view', {
            meeting_id: meeting.bot_id,
            user_id: this._getUserId(),
            timestamp: new Date().toISOString(),
        });

        if (meeting.opportunity_id && meeting.isMeetingHappened) {
            localStorage.setItem('OPPORTUNITY_NAME', meeting.opportunity_name);
            this._router.navigate([`/opportunity-detail/${meeting.opportunity_id}`, meeting.bot_id]);
        } else {
            this._infoModal.open();
        }
    }

    emailMeetingPopup(meeting: any, event: Event): void {
        event.stopPropagation();

        this._amplitudeService.trackEvent('my_past_meeting_click_notes', {
            meeting_id: meeting.bot_id,
            section: 'notes',
            user_id: this._getUserId(),
        });

        if (meeting.isMeetingHappened) {
            this.meetingBotId = meeting.bot_id;
            this.selectedEmails = meeting.attendee_emails ?? [''];
            this._emailModal.open();
        } else {
            this._infoModal.open();
        }
    }

    trackFollowUpEdit(meeting: any, changes: string): void {
        this._amplitudeService.trackEvent('my_past_meeting_edit_followups', {
            meeting_id: meeting.bot_id,
            changes,
            user_id: this._getUserId(),
        });
    }

    toggleMeetingSort(date: string): void {
        this.meetingSortOrder[date] = !this.meetingSortOrder[date];
        const group = this.allMeetings.find(g => g.date === date);
        if (group) {
            group.meetings.sort((a: any, b: any) => {
                const startA = this._getMeetingSortMs(a);
                const startB = this._getMeetingSortMs(b);
                const diff = startA - startB;
                return this.meetingSortOrder[date] ? -diff : diff;
            });
        }
    }

    getMeetingStartDisplay(meeting: any): string {
        return this._formatMeetingTime(meeting, 'start');
    }

    getMeetingEndDisplay(meeting: any): string {
        return this._formatMeetingTime(meeting, 'end');
    }

    formatMeetingDate(dateKey: string): string {
        const date = this._parseLocalDate(`${dateKey}T00:00:00`);
        return date ? this._localDateLabelFormatter.format(date) : dateKey;
    }

    formatMeetingTime(utcTime: string | null): string {
        if (!utcTime) return '--:--';
        const date = this._parseUtcDate(utcTime);
        if (!date) return '--:--';
        return this._localTimeFormatter.format(date);
    }

    getMeetingDurationLabel(meeting: any): string | null {
        const startUtc = this._getStartUtc(meeting);
        const endUtc = this._getEndUtc(meeting);
        if (!startUtc) return null;
        if (!endUtc) return 'Meeting in progress';

        const startMs = new Date(startUtc).getTime();
        const endMs = new Date(endUtc).getTime();
        if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs < startMs) return null;

        const minutes = Math.round((endMs - startMs) / 60000);
        return `${this.formatDuration(minutes)} \u2032`;
    }

    private _getStartUtc(meeting: any): string | null {
        return meeting?.start_time_UTC || null;
    }

    private _getEndUtc(meeting: any): string | null {
        return meeting?.end_time_UTC || null;
    }

    private _getMeetingDateKey(meeting: any): string | null {
        const startUtc = this._getStartUtc(meeting);
        if (startUtc) {
            const startDate = this._parseUtcDate(startUtc);
            return startDate ? this._localDateKeyFormatter.format(startDate) : null;
        }
        const localStart = meeting?.start_time;
        if (typeof localStart === 'string' && localStart.includes('T')) {
            return localStart.split('T')[0];
        }
        return null;
    }

    private _getMeetingSortMs(meeting: any): number {
        const startUtc = this._getStartUtc(meeting);
        if (startUtc) {
            return this._parseUtcDate(startUtc)?.getTime() ?? 0;
        }
        return this._parseLocalDate(meeting?.start_time)?.getTime() ?? 0;
    }

    private _formatMeetingTime(meeting: any, type: 'start' | 'end'): string {
        const utcValue = type === 'start' ? this._getStartUtc(meeting) : this._getEndUtc(meeting);
        if (utcValue) {
            return this.formatMeetingTime(utcValue);
        }

        const localValue = type === 'start' ? meeting?.start_time : meeting?.end_time;
        if (!localValue || typeof localValue !== 'string') return '--:--';
        const match = localValue.match(/T(\d{2}:\d{2})/);
        return match?.[1] ?? '--:--';
    }

    private _parseLocalDate(value: string | null | undefined): Date | null {
        if (!value) return null;
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    private _parseUtcDate(value: string | null | undefined): Date | null {
        if (!value) return null;
        const hasZoneInfo = /(?:Z|[+-]\d{2}:\d{2})$/i.test(value);
        const normalized = hasZoneInfo ? value : `${value}Z`;
        const date = new Date(normalized);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    private _getUserId(): string {
        const userInfo = sessionStorage.getItem('userInfo');
        if (!userInfo) return 'anonymous';

        try {
            const parsed = JSON.parse(userInfo);
            return parsed.user?.id || parsed.id || 'anonymous';
        } catch {
            return 'anonymous';
        }
    }
}
